import { useEffect, useState } from "react";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CloseIcon from "@mui/icons-material/Close";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { getTreasuryFormById } from "../api/treasuryFormsApi";
import {
  formatDate,
  getFieldTypeLabel,
  getFormStatusLabel,
  getFormTypeLabel,
} from "../utils/treasuryFormsFormatters";

function StatusChip({ status }) {
  const isArchived = status === "ARCHIVED";
  return (
    <Chip
      label={getFormStatusLabel(status)}
      size="small"
      sx={{
        borderRadius: "10px",
        fontWeight: 800,
        bgcolor: isArchived ? "rgba(239, 83, 80, 0.18)" : "rgba(185, 246, 202, 0.72)",
        color: isArchived ? "error.main" : "primary.900",
      }}
    />
  );
}

function MetaItem({ label, value }) {
  return (
    <Box>
      <Typography sx={{ fontSize: 11, fontWeight: 700, color: "neutral.600", textTransform: "uppercase", letterSpacing: 0.5 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: "primary.900", mt: 0.2 }}>
        {value || "—"}
      </Typography>
    </Box>
  );
}

function PreviewInput({ children, minHeight = 42 }) {
  return (
    <Box
      sx={{
        minHeight,
        px: 1.4,
        py: 1,
        borderRadius: "10px",
        border: "1px solid rgba(20, 32, 82, 0.14)",
        bgcolor: "#fff",
        color: "neutral.500",
        fontSize: 12.5,
        display: "flex",
        alignItems: minHeight > 42 ? "flex-start" : "center",
      }}
    >
      {children}
    </Box>
  );
}

function PreviewChoice({ option, multiple }) {
  const ChoiceIcon = multiple
    ? CheckBoxOutlineBlankIcon
    : RadioButtonUncheckedIcon;

  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{
        px: 1.2,
        py: 0.9,
        borderRadius: "9px",
        bgcolor: "#fff",
        border: "1px solid rgba(20, 32, 82, 0.10)",
      }}
    >
      <ChoiceIcon sx={{ fontSize: 18, color: "neutral.500" }} />
      <Typography sx={{ fontSize: 12.5, color: "neutral.700" }}>
        {option}
      </Typography>
    </Stack>
  );
}

function FormFieldPreview({ field }) {
  const options = Array.isArray(field.options) ? field.options : [];
  const typeLabel = getFieldTypeLabel(field.fieldType);

  const renderControl = () => {
    switch (field.fieldType) {
      case "LONG_TEXT":
        return <PreviewInput minHeight={72}>Respuesta larga...</PreviewInput>;
      case "NUMBER":
        return <PreviewInput>0</PreviewInput>;
      case "AMOUNT":
        return <PreviewInput>$ 0,00</PreviewInput>;
      case "SINGLE_CHOICE":
      case "MULTIPLE_CHOICE":
        return (
          <Stack spacing={0.8}>
            {(options.length ? options : ["Opción 1", "Opción 2"]).map(
              (option, index) => (
                <PreviewChoice
                  key={`${option}-${index}`}
                  option={option}
                  multiple={field.fieldType === "MULTIPLE_CHOICE"}
                />
              )
            )}
          </Stack>
        );
      case "IMAGES":
        return (
          <Stack spacing={0.7}>
            <Button
              disabled
              variant="outlined"
              startIcon={<AddPhotoAlternateOutlinedIcon />}
              sx={{
                alignSelf: "flex-start",
                borderRadius: "10px",
                textTransform: "none",
                fontSize: 12,
              }}
            >
              Agregar imágenes
            </Button>
            <Typography sx={{ fontSize: 10.5, color: "neutral.500" }}>
              Hasta 3 imágenes JPG, JPEG o PNG
            </Typography>
          </Stack>
        );
      case "CONCEPT_AMOUNT_LIST":
        return (
          <Stack spacing={0.8}>
            {[1, 2].map((row) => (
              <Stack key={row} direction="row" spacing={0.8}>
                <Box sx={{ flex: 1 }}>
                  <PreviewInput>Concepto</PreviewInput>
                </Box>
                <Box sx={{ width: 105 }}>
                  <PreviewInput>$ Importe</PreviewInput>
                </Box>
              </Stack>
            ))}
            <Typography sx={{ fontSize: 10.5, color: "neutral.500" }}>
              Hasta 20 filas
            </Typography>
          </Stack>
        );
      case "SHORT_TEXT":
      default:
        return <PreviewInput>Respuesta corta...</PreviewInput>;
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.4,
        borderRadius: "14px",
        bgcolor: "rgba(255,255,255,0.96)",
        border: "1px solid rgba(20, 32, 82, 0.10)",
      }}
    >
      <Stack spacing={1}>
        <Stack
          direction="row"
          spacing={1}
          alignItems="flex-start"
          justifyContent="space-between"
        >
          <Typography
            sx={{ fontSize: 13, fontWeight: 800, color: "primary.900" }}
          >
            {field.label}
          </Typography>
          <Stack spacing={0.35} alignItems="flex-end" sx={{ flexShrink: 0 }}>
            <Chip
              label={typeLabel}
              size="small"
              sx={{ height: 20, fontSize: 10, fontWeight: 700 }}
            />
            {field.isRequired ? (
              <Typography
                sx={{ fontSize: 9.5, fontWeight: 800, color: "error.main" }}
              >
                (Obligatorio)
              </Typography>
            ) : null}
          </Stack>
        </Stack>
        {renderControl()}
      </Stack>
    </Paper>
  );
}

export default function PlantillaDetailModal({
  open,
  formId,
  formSummary,
  onClose,
}) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchDetail = async (id, signal) => {
    if (!id) return;
    setLoading(true);
    setError("");
    try {
      const response = await getTreasuryFormById(id, { signal });
      const payload = response?.data && !("id" in response) ? response.data : response;
      setDetail(payload);
    } catch (err) {
      if (signal?.aborted) return;
      setError(
        err?.response?.data?.message ||
          err?.data?.message ||
          err?.message ||
          "No se pudo cargar el detalle de la plantilla."
      );
    } finally {
      if (!signal?.aborted) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (!open || !formId) {
      setDetail(null);
      setError("");
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    fetchDetail(formId, controller.signal);

    return () => {
      controller.abort();
    };
  }, [open, formId]);

  const currentForm = detail || formSummary;
  const currentVersion = detail?.currentVersion;
  const fields = currentVersion?.fields || [];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "20px",
          p: { xs: 1, sm: 2 },
          bgcolor: "neutral.50",
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 1,
          px: { xs: 1, sm: 2 },
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Typography sx={{ fontSize: 18, fontWeight: 900, color: "primary.900" }}>
            Vista previa del formulario
          </Typography>
          {currentForm?.status ? <StatusChip status={currentForm.status} /> : null}
        </Stack>

        <Tooltip title="Cerrar">
          <IconButton
            onClick={onClose}
            aria-label="Cerrar modal de detalle"
            size="small"
            sx={{
              color: "neutral.600",
              "&:hover": { color: "primary.900", bgcolor: "neutral.200" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </DialogTitle>

      <DialogContent sx={{ px: { xs: 1, sm: 2 }, py: 1 }}>
        <Stack spacing={2.5}>
          {loading ? (
            <Stack alignItems="center" spacing={1.5} sx={{ py: 6 }}>
              <CircularProgress size={32} />
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: "primary.900" }}>
                Cargando información de la plantilla...
              </Typography>
            </Stack>
          ) : null}

          {!loading && error ? (
            <Alert
              severity="error"
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => fetchDetail(formId)}
                  sx={{ fontWeight: 800 }}
                >
                  Reintentar
                </Button>
              }
              sx={{ borderRadius: "14px" }}
            >
              {error}
            </Alert>
          ) : null}

          {!loading && !error && currentForm ? (
            <>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: "16px",
                  bgcolor: "rgba(255, 255, 255, 0.95)",
                  border: "1px solid rgba(20, 32, 82, 0.08)",
                }}
              >
                <Stack spacing={1.8}>
                  <Box>
                    <Typography sx={{ fontSize: 18, fontWeight: 900, color: "primary.900" }}>
                      {currentForm.name || "Sin nombre"}
                    </Typography>
                    {currentForm.description ? (
                      <Typography sx={{ fontSize: 13, color: "neutral.700", mt: 0.5 }}>
                        {currentForm.description}
                      </Typography>
                    ) : (
                      <Typography sx={{ fontSize: 13, color: "neutral.500", fontStyle: "italic", mt: 0.5 }}>
                        Sin descripción
                      </Typography>
                    )}
                  </Box>

                  <Divider />

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm: "repeat(2, minmax(0, 1fr))",
                        md: "repeat(4, minmax(0, 1fr))",
                      },
                      gap: 2,
                    }}
                  >
                    <MetaItem label="Tipo" value={getFormTypeLabel(currentForm.type)} />
                    <MetaItem
                      label="Versión vigente"
                      value={
                        currentVersion?.versionNumber
                          ? `Versión ${currentVersion.versionNumber}`
                          : currentForm.currentVersionNumber
                          ? `Versión ${currentForm.currentVersionNumber}`
                          : "Versión 1"
                      }
                    />
                    <MetaItem
                      label="Última modificación"
                      value={formatDate(currentForm.updatedAt)}
                    />
                    <MetaItem
                      label="Creado por"
                      value={
                        currentForm.author?.displayName ||
                        currentForm.author?.name ||
                        "—"
                      }
                    />
                  </Box>
                </Stack>
              </Paper>

              <Box sx={{ mt: 1 }}>
                <Typography
                  sx={{
                    fontSize: 15,
                    fontWeight: 900,
                    color: "primary.900",
                    mb: 1.5,
                  }}
                >
                  Campos del formulario ({fields.length})
                </Typography>

                <Box
                  data-testid="form-preview"
                  sx={{
                    width: "100%",
                    p: { xs: 1, sm: 2 },
                    borderRadius: "16px",
                    bgcolor: "rgba(238, 240, 246, 0.6)",
                    border: "1px solid rgba(20, 32, 82, 0.08)",
                  }}
                >
                  <Stack spacing={1.5}>
                    {fields.length === 0 ? (
                      <Paper
                        elevation={0}
                        sx={{ p: 4, borderRadius: "14px", textAlign: "center", bgcolor: "#fff" }}
                      >
                        <Typography sx={{ fontSize: 13, color: "neutral.600" }}>
                          Esta plantilla no tiene campos definidos.
                        </Typography>
                      </Paper>
                    ) : (
                      fields.map((field, index) => (
                        <FormFieldPreview key={field.id || index} field={field} />
                      ))
                    )}
                  </Stack>
                </Box>
              </Box>
            </>
          ) : null}
        </Stack>
      </DialogContent>

      <Divider />
      <Box sx={{ p: 2, display: "flex", justifyContent: "flex-end" }}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            borderRadius: "24px",
            textTransform: "none",
            fontWeight: 800,
            px: 3.5,
            py: 0.8,
            bgcolor: "primary.900",
            color: "#fff",
            "&:hover": { bgcolor: "primary.800" },
          }}
        >
          Cerrar
        </Button>
      </Box>
    </Dialog>
  );
}
